import { defineStore, acceptHMRUpdate } from 'pinia';
import { set } from '../lib/SCORM';
import { ref, computed, nextTick } from 'vue';
import isEditModeAvailable from '../lib/isEditModeAvailable';
import { dispatchUcEvent } from '../lib/useUcEvent';
import useQuizzes from './useQuizzes';

export type Mode = 'view' | 'edit' | 'design';

const useEditMode = defineStore('editMode', () => {
  // runtime availability is computed at load from URL/SCORM flags,
  // but in authoring we allow toggling this at runtime
  const available = ref(isEditModeAvailable);
  const addPageEnabled = ref(true);
  const mode = ref<Mode>('view');
  const enabled = computed(() => mode.value === 'edit');
  const activeScreenId = ref<string | null>(null);
  const activeEditPath = ref<string | null>(null);
  // Suspends inline editing without leaving edit mode — see setAuthorInlineEditLocked.
  const inlineEditLocked = ref(false);

  const setMode = async (toMode: Mode) => {
    await new Promise(r => setTimeout(r, 50));

    // const prevMode = this.mode;
    mode.value = toMode;

    // if (toMode === 'edit') {
    //   set('locale', 'en-GB');
    // }

    dispatchUcEvent('uc:editmodechange', { mode: toMode });

    nextTick(async () => {
      await nextTick();
      await nextTick();

      // refresh scrolltriggers so that hidden screens are animated when switching to edit mode
      document.querySelectorAll<HTMLElement>('.scroller').forEach(el => {
        const page = el.closest('.page') as UcPageElement;
        page?.refreshSections();
      });
    });
  };

  // Allow author preview to toggle availability via a window hook (kept here)
  if (__AUTHORING__) {
    // @ts-ignore
    window.setAuthorInlineEdit = (enabled: boolean) => {
      available.value = enabled;
      mode.value = enabled ? 'edit' : 'view';
      // also surface a simple boolean for isEditModeAvailable.ts to read without import cycles
      // @ts-ignore
      window.editModeAvailable = enabled;
    };
    // @ts-ignore
    window.setAuthorAddPageEnabled = (enabled: boolean) => {
      addPageEnabled.value = enabled;
    };
    // Disable inline editing while keeping mode === 'edit', so the agent's
    // section unlocking and the edit-mode visuals are preserved.
    // @ts-ignore
    window.setAuthorInlineEditLocked = (locked: boolean) => {
      inlineEditLocked.value = locked;
    };
    // @ts-ignore
    window.unlockAdaptiveForEditMode = () => {
      const quizzes = useQuizzes();
      if (quizzes.isAdaptiveCourse && !quizzes.unlockedForEditMode)
        quizzes.setAllQuizzesForEditMode(true);
    };

    window.dispatchEvent(new CustomEvent('uc:inlineedithooksready'));

    try {
      document.body.classList.remove('mode-authoring');
    } catch {}
  }

  // set the active edit screen ID and edit path
  const setEditScreen = async (screenId: string, editPath?: string) => {
    activeScreenId.value = screenId;
    activeEditPath.value = editPath || null;

    await nextTick();
    return true;
  };

  return {
    available,
    addPageEnabled,
    mode,
    enabled,
    inlineEditLocked,
    setMode,
    setEditScreen,
    activeScreenId,
    activeEditPath
  };
});

export default useEditMode;

if (import.meta.hot) {
  import.meta.hot.accept(acceptHMRUpdate(useEditMode, import.meta.hot));
}
